import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  useColorModeValue,
  Flex,
  HStack,
  Button,
  Icon,
  useToast,
  VStack,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Select,
  Tooltip,
  Progress,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { FaSync, FaDownload, FaBoxes, FaChartBar, FaChartLine } from 'react-icons/fa';
import analyticsService from '../../services/analyticsService';
import Card from '../card/Cards';

const COLORS = ['#3182CE', '#38B2AC', '#805AD5', '#DD6B20', '#D53F8C', '#38A169', '#E53E3E', '#3182CE'];

const PartsConsumptionWidget = () => {
  const [consumptionData, setConsumptionData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [timeRange, setTimeRange] = useState('30');
  const [topN, setTopN] = useState(10);
  const [chartType, setChartType] = useState('bar');
  const [activeTab, setActiveTab] = useState(0);

  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const barFillColor = useColorModeValue('#3182CE', '#63B3ED');
  const insightsBg = useColorModeValue('blue.50', 'blue.900');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { days: parseInt(timeRange), limit: topN };
      
      const [consumptionRes, trendRes] = await Promise.all([
        analyticsService.getWidgetData('parts-consumption', params),
        analyticsService.getWidgetData('parts-consumption-trend', params),
      ]);

      if (consumptionRes.success && Array.isArray(consumptionRes.data)) {
        // Sort by usage (descending for most used)
        const sortedData = consumptionRes.data
          .sort((a, b) => b.total_used - a.total_used)
          .slice(0, topN)
          .map((item, index) => ({
            ...item,
            rank: index + 1,
            percentage: 0, // Will be calculated
          }));

        // Calculate percentages
        const total = sortedData.reduce((sum, item) => sum + item.total_used, 0);
        sortedData.forEach(item => {
          item.percentage = ((item.total_used / total) * 100).toFixed(1);
        });

        setConsumptionData(sortedData);
      } else {
        throw new Error(consumptionRes.message || 'Invalid data format received.');
      }

      if (trendRes.success && Array.isArray(trendRes.data)) {
        setTrendData(trendRes.data);
      }

      setLastRefresh(new Date());
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch parts consumption data.';
      setError(errorMsg);
      console.error('Parts consumption error:', err);
      toast({
        title: 'Error Loading Data',
        description: errorMsg,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [timeRange, topN, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast({
      title: 'Data Refreshed',
      description: 'Parts consumption data has been updated.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleExport = () => {
    toast({
      title: 'Export Started',
      description: 'Preparing parts consumption report...',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
    // Implementation would trigger actual export
  };

  // Calculate summary statistics
  const stats = useMemo(() => {
    if (consumptionData.length === 0) return null;

    const totalUsed = consumptionData.reduce((sum, item) => sum + item.total_used, 0);
    const avgUsage = totalUsed / consumptionData.length;
    const topPart = consumptionData[0];
    const topThreePercentage = consumptionData
      .slice(0, 3)
      .reduce((sum, item) => sum + parseFloat(item.percentage), 0);

    return {
      totalUsed,
      avgUsage: avgUsage.toFixed(1),
      topPart: topPart?.name || 'N/A',
      topPartUsage: topPart?.total_used || 0,
      topThreePercentage: topThreePercentage.toFixed(1),
    };
  }, [consumptionData]);

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={consumptionData}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis type="category" dataKey="name" width={150} />
        <RechartsTooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              return (
                <Box bg={cardBg} p={3} borderRadius="md" boxShadow="lg" borderWidth="1px">
                  <Text fontWeight="bold" mb={1}>{data.name}</Text>
                  <Text fontSize="sm">Used: {data.total_used} units</Text>
                  <Text fontSize="sm">Share: {data.percentage}%</Text>
                  <Text fontSize="sm">Rank: #{data.rank}</Text>
                </Box>
              );
            }
            return null;
          }}
        />
        <Legend />
        <Bar dataKey="total_used" fill={barFillColor} name="Total Units Used">
          {consumptionData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <PieChart>
        <Pie
          data={consumptionData}
          dataKey="total_used"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={120}
          label={({ name, percentage }) => `${name}: ${percentage}%`}
          labelLine={true}
        >
          {consumptionData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <RechartsTooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              return (
                <Box bg={cardBg} p={3} borderRadius="md" boxShadow="lg" borderWidth="1px">
                  <Text fontWeight="bold" mb={1}>{data.name}</Text>
                  <Text fontSize="sm">Used: {data.total_used} units</Text>
                  <Text fontSize="sm">Share: {data.percentage}%</Text>
                </Box>
              );
            }
            return null;
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderTrendChart = () => {
    if (!trendData || trendData.length === 0) {
      return (
        <Flex justify="center" align="center" h="400px">
          <Text color="gray.500">No trend data available for this period.</Text>
        </Flex>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="total_used"
            stroke="#3182CE"
            strokeWidth={2}
            name="Daily Usage"
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Flex justify="center" align="center" h="400px">
          <VStack>
            <Spinner size="xl" />
            <Text>Loading consumption data...</Text>
          </VStack>
        </Flex>
      );
    }

    if (error) {
      return (
        <Alert status="error">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold">Error Loading Data</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
          <Button size="sm" onClick={handleRefresh}>
            Retry
          </Button>
        </Alert>
      );
    }

    if (consumptionData.length === 0) {
      return (
        <Flex justify="center" align="center" h="400px" flexDirection="column">
          <Icon as={FaBoxes} boxSize={12} color="gray.400" mb={4} />
          <Text fontSize="lg" fontWeight="semibold" mb={2}>
            No Consumption Data Available
          </Text>
          <Text fontSize="sm" color="gray.500">
            No parts have been used in the selected time period.
          </Text>
        </Flex>
      );
    }

    return (
      <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>
            <Icon as={FaChartBar} mr={2} />
            Bar Chart
          </Tab>
          <Tab>
            <Icon as={FaBoxes} mr={2} />
            Distribution
          </Tab>
          <Tab>
            <Icon as={FaChartLine} mr={2} />
            Trend
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>{renderBarChart()}</TabPanel>
          <TabPanel px={0}>{renderPieChart()}</TabPanel>
          <TabPanel px={0}>{renderTrendChart()}</TabPanel>
        </TabPanels>
      </Tabs>
    );
  };

  return (
    <Card p={5} borderWidth="1px" borderColor={borderColor}>
      <VStack spacing={5} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" flexWrap="wrap">
          <Box>
            <Heading as="h3" size="md" color={textColor} mb={1}>
              Top Parts Consumption Analysis
            </Heading>
            {lastRefresh && (
              <Text fontSize="xs" color="gray.500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </Text>
            )}
          </Box>
          <HStack spacing={2}>
            <Button
              leftIcon={<Icon as={FaSync} />}
              size="sm"
              onClick={handleRefresh}
              isLoading={refreshing}
              variant="outline"
            >
              Refresh
            </Button>
            <Button
              leftIcon={<Icon as={FaDownload} />}
              size="sm"
              onClick={handleExport}
              colorScheme="blue"
            >
              Export
            </Button>
          </HStack>
        </Flex>

        {/* Summary Statistics */}
        {stats && (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat
              bg={cardBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              size="sm"
            >
              <StatLabel fontSize="xs">Total Used</StatLabel>
              <StatNumber fontSize="lg">{stats.totalUsed}</StatNumber>
              <StatHelpText fontSize="xs">
                Last {timeRange} days
              </StatHelpText>
            </Stat>

            <Stat
              bg={cardBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              size="sm"
            >
              <StatLabel fontSize="xs">Average Usage</StatLabel>
              <StatNumber fontSize="lg">{stats.avgUsage}</StatNumber>
              <StatHelpText fontSize="xs">
                Per part type
              </StatHelpText>
            </Stat>

            <Stat
              bg={cardBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              size="sm"
            >
              <StatLabel fontSize="xs">Top Part</StatLabel>
              <Tooltip label={stats.topPart}>
                <StatNumber fontSize="sm" noOfLines={1}>
                  {stats.topPart}
                </StatNumber>
              </Tooltip>
              <StatHelpText fontSize="xs">
                {stats.topPartUsage} units used
              </StatHelpText>
            </Stat>

            <Stat
              bg={cardBg}
              p={3}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
              size="sm"
            >
              <StatLabel fontSize="xs">Top 3 Share</StatLabel>
              <StatNumber fontSize="lg" color="blue.500">
                {stats.topThreePercentage}%
              </StatNumber>
              <StatHelpText fontSize="xs">
                Of total consumption
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* Filters */}
        <HStack spacing={3} flexWrap="wrap">
          <Box>
            <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.600">
              Time Range
            </Text>
            <Select
              size="sm"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              w="130px"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last Year</option>
            </Select>
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="semibold" mb={1} color="gray.600">
              Show Top
            </Text>
            <Select
              size="sm"
              value={topN}
              onChange={(e) => setTopN(parseInt(e.target.value))}
              w="100px"
            >
              <option value="5">Top 5</option>
              <option value="10">Top 10</option>
              <option value="15">Top 15</option>
              <option value="20">Top 20</option>
            </Select>
          </Box>
        </HStack>

        {/* Content */}
        {renderContent()}

        {/* Insights */}
        {consumptionData.length > 0 && (
          <Box
            p={3}
            bg={insightsBg}
            borderRadius="md"
            borderLeftWidth="4px"
            borderLeftColor="blue.500"
          >
            <HStack spacing={2} mb={2}>
              <Icon as={FaChartLine} color="blue.500" />
              <Text fontSize="sm" fontWeight="bold">
                Key Insights
              </Text>
            </HStack>
            <VStack align="stretch" spacing={1}>
              <Text fontSize="xs">
                • <strong>{stats.topPart}</strong> is your most consumed part with{' '}
                <strong>{stats.topPartUsage}</strong> units used
              </Text>
              <Text fontSize="xs">
                • Top 3 parts account for <strong>{stats.topThreePercentage}%</strong> of total consumption
              </Text>
              <Text fontSize="xs">
                • Average usage per part type is <strong>{stats.avgUsage}</strong> units
              </Text>
              {parseFloat(stats.topThreePercentage) > 60 && (
                <Text fontSize="xs" color="orange.600" fontWeight="semibold">
                  ⚠️ High concentration risk: Consider diversifying part suppliers
                </Text>
              )}
            </VStack>
          </Box>
        )}

        {/* Footer Note */}
        {consumptionData.length > 0 && (
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Data shows the {topN} most frequently used parts over the last {timeRange} days.
            Switch tabs to view different visualizations.
          </Text>
        )}
      </VStack>
    </Card>
  );
};

export default PartsConsumptionWidget;